/**
 * DatasetsTable Component
 *
 * Renders the datasets list in a table format with sortable columns and
 * responsive design. Follows patterns from SurveyCompactTable.
 */

import { useState, useMemo } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import { ArrowUpDown, ArrowUp, ArrowDown, File, Check, Ban, FileWarning } from "lucide-react"
import { Dataset, prettifyDomainValue, PERMITTED_USERS_ALL_SENTINEL } from "@/services/datasetsService"

/** Column definition for the datasets table. */
interface ColumnDef {
  /** Unique key matching a Dataset property or computed field. */
  key: string
  /** Display label for the column header. */
  label: string
  /** Whether the column is sortable. Defaults to true. */
  sortable?: boolean
  /** Minimum width in pixels (optional). */
  minWidth?: number
  /** Maximum width in pixels (optional). */
  maxWidth?: number
}

/** Available columns for the datasets table. */
const columns: ColumnDef[] = [
  { key: "title", label: "Title", minWidth: 200, maxWidth: 350 },
  { key: "dataTopic", label: "Data Topic", minWidth: 120 },
  { key: "unitOfAnalysis", label: "Unit of Analysis", minWidth: 130 },
  { key: "countries", label: "Countries", minWidth: 120 },
  { key: "publishable", label: "Publishable", minWidth: 100 },
  { key: "shared", label: "Shared With", minWidth: 70 },
  { key: "owner", label: "Owner", minWidth: 120 },
  { key: "uploadDate", label: "Uploaded", minWidth: 100 },
  { key: "biblioId", label: "Biblio ID", minWidth: 80 },
  { key: "fileCount", label: "Files", sortable: true, minWidth: 60 },
]

/** Sort direction type. */
type SortDirection = "asc" | "desc"

/** Props for the DatasetsTable component. */
interface DatasetsTableProps {
  /** The datasets to display. */
  datasets: Dataset[]
  /** Map of user sub -> display name for owner rendering. */
  userNameMap: Map<string, string>
  /** Current user's Auth0 sub claim (for highlighting owned datasets). */
  currentUserSub: string
  /** The currently selected dataset ID (for highlighting the row). */
  selectedDatasetId?: string
  /** Callback when a dataset row is clicked. */
  onDatasetSelect: (dataset: Dataset) => void
}

/**
 * Format an ISO date string to a human-readable short date.
 *
 * @param isoDate - ISO 8601 date string.
 * @returns Formatted date string (e.g., "15 Oct 2024").
 */
function formatDate(isoDate: string): string {
  try {
    const date = new Date(isoDate)
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
  } catch {
    return isoDate
  }
}

/**
 * Get a sortable value for a dataset field.
 *
 * @param dataset - The dataset record.
 * @param key - The field key to sort by.
 * @param userNameMap - Map of user sub -> display name.
 * @returns A value suitable for sorting comparison.
 */
function getSortValue(
  dataset: Dataset,
  key: string,
  userNameMap: Map<string, string>
): string | number {
  switch (key) {
    case "title":
      return dataset.title.toLowerCase()
    case "dataTopic":
      return dataset.dataTopic.join(", ").toLowerCase()
    case "unitOfAnalysis":
      return (dataset.unitOfAnalysis ?? []).join(", ").toLowerCase()
    case "countries":
      return (dataset.countries ?? []).join(", ").toLowerCase()
    case "owner":
      return (userNameMap.get(dataset.owner) ?? dataset.owner).toLowerCase()
    case "publishable":
      return (dataset.publishable ?? "").toLowerCase()
    case "shared":
      // Sort by shared count for owners, by whether shared with me for others
      return (dataset.permittedUsers ?? []).length
    case "uploadDate":
      return dataset.uploadDate
    case "biblioId":
      return dataset.biblioId ?? 0
    case "fileCount":
      return (dataset.versions[String(dataset.currentVersion)]?.fileKeys ?? []).length
    default:
      return ""
  }
}

/**
 * DatasetsTable — renders datasets in a sortable table.
 *
 * @param props - Component props.
 * @param props.datasets - Datasets to display (already filtered).
 * @param props.userNameMap - Map of user sub to display name.
 * @param props.currentUserSub - Current user's sub for ownership indicator.
 * @param props.selectedDatasetId - ID of the currently selected dataset.
 * @param props.onDatasetSelect - Callback when a row is clicked.
 */
export default function DatasetsTable({
  datasets,
  userNameMap,
  currentUserSub,
  selectedDatasetId,
  onDatasetSelect,
}: DatasetsTableProps): React.JSX.Element {
  // Sort state
  const [sortKey, setSortKey] = useState<string>("uploadDate")
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc")

  /**
   * Handle column header click for sorting.
   *
   * @param key - The column key to sort by.
   */
  const handleSort = (key: string): void => {
    if (sortKey === key) {
      // Toggle direction if same column
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
    } else {
      // New column — default to ascending (except dates which default to desc)
      setSortKey(key)
      setSortDirection(key === "uploadDate" ? "desc" : "asc")
    }
  }

  // Sort the datasets
  const sortedDatasets = useMemo<Dataset[]>(() => {
    return [...datasets].sort((a, b) => {
      const aVal = getSortValue(a, sortKey, userNameMap)
      const bVal = getSortValue(b, sortKey, userNameMap)

      let comparison: number
      if (typeof aVal === "number" && typeof bVal === "number") {
        comparison = aVal - bVal
      } else {
        comparison = String(aVal).localeCompare(String(bVal))
      }

      return sortDirection === "asc" ? comparison : -comparison
    })
  }, [datasets, sortKey, sortDirection, userNameMap])

  /**
   * Render the sort indicator icon for a column header.
   *
   * @param key - The column key.
   * @returns The appropriate icon element.
   */
  const renderSortIcon = (key: string): React.ReactNode => {
    if (sortKey !== key) {
      return <ArrowUpDown className="size-3.5 ml-1 text-muted-foreground/50" />
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="size-3.5 ml-1" />
    ) : (
      <ArrowDown className="size-3.5 ml-1" />
    )
  }

  /**
   * Render a cell value based on the column key.
   *
   * @param dataset - The dataset record.
   * @param key - The column key.
   * @returns The rendered cell content.
   */
  const renderCell = (dataset: Dataset, key: string): React.ReactNode => {
    switch (key) {
      case "title":
        return (
          <div className="flex flex-col gap-0.5 max-w-[250px]">
            <span className="font-medium truncate">{dataset.title}</span>
            {dataset.description && (
              <span className="text-xs text-muted-foreground truncate">
                {dataset.description}
              </span>
            )}
          </div>
        )

      case "dataTopic":
        return (
          <div className="flex flex-wrap gap-1">
            {dataset.dataTopic.slice(0, 2).map(topic => (
              <Badge key={topic} className="bg-primary/25 text-foreground">
                {topic}
              </Badge>
            ))}
            {dataset.dataTopic.length > 2 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="text-xs cursor-default">
                    +{dataset.dataTopic.length - 2}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  {dataset.dataTopic.slice(2).map(prettifyDomainValue).join(", ")}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )

      case "unitOfAnalysis":
        // Mirrors the dataTopic rendering: show the first two as badges and
        // collapse the rest into a "+N" tooltip when there are more.
        if (!dataset.unitOfAnalysis || dataset.unitOfAnalysis.length === 0) {
          return <span className="text-muted-foreground">—</span>
        }
        return (
          <div className="flex flex-wrap gap-1">
            {dataset.unitOfAnalysis.slice(0, 2).map((unit) => (
              <Badge key={unit} variant="secondary" className="text-xs">
                {unit}
              </Badge>
            ))}
            {dataset.unitOfAnalysis.length > 2 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="text-xs cursor-default">
                    +{dataset.unitOfAnalysis.length - 2}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  {dataset.unitOfAnalysis.slice(2).join(", ")}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )

      case "countries":
        if (!dataset.countries || dataset.countries.length === 0) {
          return <span className="text-muted-foreground">—</span>
        }
        return (
          <div className="flex flex-wrap gap-1">
            {dataset.countries.slice(0, 2).map((iso3) => (
              <Badge key={iso3} variant="outline" className="text-xs">
                {iso3}
              </Badge>
            ))}
            {dataset.countries.length > 2 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="text-xs cursor-default">
                    +{dataset.countries.length - 2}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>{dataset.countries.slice(2).join(", ")}</TooltipContent>
              </Tooltip>
            )}
          </div>
        )

      case "owner":
        const ownerName = userNameMap.get(dataset.owner) ?? dataset.owner
        const isOwnedByMe = dataset.owner === currentUserSub
        return (
          <span className={isOwnedByMe ? "font-medium" : ""}>
            {isOwnedByMe ? "You" : ownerName}
          </span>
        )

      case "publishable":
        if (!dataset.publishable) {
          return <span className="text-muted-foreground">—</span>
        }
        const hasSharingAgreement =
          (dataset.sharingAgreementFileKeys ?? []).length > 0 ||
          !!dataset.sharingAgreementText?.trim()

        if (dataset.publishable === "PERM_TO_RELEASE") {
          return (
            <div className="flex items-center gap-1.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Check className="size-4 text-green-600" />
                </TooltipTrigger>
                <TooltipContent>{prettifyDomainValue(dataset.publishable)}</TooltipContent>
              </Tooltip>
              {hasSharingAgreement && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <FileWarning className="size-4 text-amber-500" />
                  </TooltipTrigger>
                  <TooltipContent>Has sharing agreement</TooltipContent>
                </Tooltip>
              )}
            </div>
          )
        }
        return (
          <div className="flex items-center gap-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Ban className="size-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>{prettifyDomainValue(dataset.publishable)}</TooltipContent>
            </Tooltip>
            {hasSharingAgreement && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <FileWarning className="size-4 text-amber-500" />
                </TooltipTrigger>
                <TooltipContent>Has sharing agreement</TooltipContent>
              </Tooltip>
            )}
          </div>
        )

      case "shared":
        const permittedUsers = dataset.permittedUsers ?? []
        const isSharedWithAll = permittedUsers.includes(PERMITTED_USERS_ALL_SENTINEL)
        const isSharedWithMe = isSharedWithAll || permittedUsers.includes(currentUserSub)

        if (permittedUsers.length === 0) {
          return <span className="text-muted-foreground">—</span>
        }

        if (isSharedWithAll) {
          return (
            <Badge variant="outline" className="text-xs text-foreground">
              All
            </Badge>
          )
        }

        const otherSharedWith = isSharedWithMe
          ? permittedUsers.filter(u => u !== currentUserSub)
          : permittedUsers;

        return (
          <div className="flex flex-wrap gap-1">
            {permittedUsers.includes(currentUserSub) && (
              <Badge variant="outline" className={"text-xs text-foreground"}>
                You
              </Badge>
            )}
            {otherSharedWith.length > 0 && (
              <Badge variant="outline">+{otherSharedWith.length}</Badge>
            )}
          </div>
        )
      case "uploadDate":
        return (
          <span className="text-sm text-muted-foreground">
            {formatDate(dataset.uploadDate)}
          </span>
        )

      case "biblioId":
        if (dataset.biblioId === undefined) {
          return <span className="text-muted-foreground">—</span>
        }
        return <span>{dataset.biblioId}</span>
      case "fileCount":
        return (
          <div className="flex items-center gap-1.5">
            <File className="size-3.5 text-muted-foreground" />
            <span>{(dataset.versions[String(dataset.currentVersion)]?.fileKeys ?? []).length}</span>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto border border-border/50">
      <Table className="relative border-collapse">
        <TableHeader className="sticky top-0 z-20 bg-card shadow-sm">
          <TableRow>
            {columns.map((col) => {
              const isSortable = col.sortable !== false
              return (
                <TableHead
                  key={col.key}
                  style={{
                    ...(col.minWidth ? { minWidth: `${col.minWidth}px` } : {}),
                    ...(col.maxWidth ? { maxWidth: `${col.maxWidth}px` } : {}),
                  }}
                  className={isSortable ? "cursor-pointer select-none" : ""}
                  onClick={isSortable ? () => handleSort(col.key) : undefined}
                >
                  <div className="flex items-center">
                    {col.label}
                    {isSortable && renderSortIcon(col.key)}
                  </div>
                </TableHead>
              )
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedDatasets.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="text-center text-muted-foreground py-8"
              >
                No datasets found.
              </TableCell>
            </TableRow>
          ) : (
            sortedDatasets.map((dataset) => {
              const isSelected = dataset.datasetId === selectedDatasetId
              return (
                <TableRow
                  key={dataset.datasetId}
                  className={`cursor-pointer transition-colors ${
                    isSelected
                      ? "bg-accent"
                      : "hover:bg-accent/30"
                  }`}
                  onClick={() => onDatasetSelect(dataset)}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      style={col.maxWidth ? { maxWidth: `${col.maxWidth}px` } : undefined}
                    >
                      {renderCell(dataset, col.key)}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
    </div>
  )
}
